import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { UserProfile } from '@/components/Sidebar/UserProfile'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useDashboard } from '@/queries/useDashboard'
import { useAuthStore } from '@/store/authStore'
import { useSelectionStore } from '@/store/selectionStore'
import { useAuth, useUser } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  LayoutDashboard,
  LogOut,
  ShoppingCart,
  Store,
  UtensilsCrossed,
} from 'lucide-react'
import { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import DashboardContext, { UserRole } from './DashboardContext'

interface AdminLayoutProps {
  children: ReactNode
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { signOut, isLoaded } = useAuth()
  const { user } = useUser()
  const { t } = useTranslation('admin')
  const clearAuth = useAuthStore((state) => state.clearAuth)
  
  // Use selection store for branch selection (restaurant selection is in URL)
  const { selectedBranch, setSelectedBranch } = useSelectionStore()
  const selectedBranchIdFromStore = selectedBranch?.id

  // Navigation items with translated labels
  const navigationItems = [
    { path: '/admin/dashboard', labelKey: 'navigation.dashboard', icon: LayoutDashboard },
    { path: '/admin/restaurants', labelKey: 'navigation.restaurants', icon: Store },
    { path: '/admin/branches', labelKey: 'navigation.branches', icon: Building2 },
    { path: '/admin/menu-items', labelKey: 'navigation.menuItems', icon: UtensilsCrossed },
    { path: '/admin/dishes', labelKey: 'navigation.dishes', icon: UtensilsCrossed },
    { path: '/admin/orders', labelKey: 'navigation.orders', icon: ShoppingCart },
  ]

  // Detect user role from Clerk metadata
  const userRoles = (user?.publicMetadata?.roles as string[] | undefined) || []
  const isSuperAdmin = Array.isArray(userRoles) && userRoles.includes('super_admin')
  const isBranchManager = Array.isArray(userRoles) && userRoles.includes('branch_manager')

  // Determine role
  const role: UserRole = isSuperAdmin
    ? 'super_admin'
    : isBranchManager
    ? 'branch_manager'
    : 'restaurant_admin'

  // For branch managers, get assigned branch ID from Clerk metadata
  const assignedBranchId = isBranchManager
    ? (user?.publicMetadata?.assignedBranchId as string | undefined)
    : undefined

  // Read URL query parameters for context (only restaurantId, branchId is in store)
  const restaurantIdFromUrl = searchParams.get('restaurantId')

  // For branch manager, ignore URL params (always use token context)
  // For restaurant admin, ignore restaurantId (always use token context)
  // For super admin, use URL params if provided
  const effectiveRestaurantId =
    role === 'branch_manager' || role === 'restaurant_admin'
      ? null // Will use token context
      : restaurantIdFromUrl || null

  // BranchId comes from store, not URL
  const effectiveBranchId = null // Will use token context or store

  // Fetch dashboard data to get context info
  // For super admin, pass selectedBranchId from store to dashboard query
  // For branch manager, pass assignedBranchId from Clerk metadata to dashboard query
  // For restaurant admin, pass selectedBranchId from store to dashboard query (to get branch name)
  // Note: Backend needs branchId to fetch branch name, so we pass it explicitly
  const effectiveBranchIdForDashboard = 
    role === 'super_admin' && selectedBranchIdFromStore
      ? selectedBranchIdFromStore
      : role === 'branch_manager' && assignedBranchId
      ? assignedBranchId
      : role === 'restaurant_admin' && selectedBranchIdFromStore
      ? selectedBranchIdFromStore
      : effectiveBranchId
  
  const { data: dashboardData, isLoading: isLoadingDashboard } = useDashboard(effectiveRestaurantId, effectiveBranchIdForDashboard)

  // Extract context from dashboard data for names
  const restaurantName =
    dashboardData?.context?.restaurantName || dashboardData?.restaurants.info?.name || 'Loading...'
  const branchName = dashboardData?.context?.branchName
  
  // Use URL params for restaurantId, store for branchId
  const currentRestaurantId =
    restaurantIdFromUrl || 
    dashboardData?.context?.restaurantId || 
    dashboardData?.restaurants.info?.id || 
    ''
  // BranchId comes from store (sidebar selection), fallback to dashboard context for display
  // For branch managers, use assignedBranchId from Clerk metadata
  const currentBranchId = 
    role === 'branch_manager' && assignedBranchId
      ? assignedBranchId
      : selectedBranchIdFromStore || dashboardData?.context?.branchId || undefined

  // Handle context changes
  const handleRestaurantChange = (newRestaurantId: string | null) => {
    const newParams = new URLSearchParams(searchParams)
    if (newRestaurantId) {
      newParams.set('restaurantId', newRestaurantId)
    } else {
      newParams.delete('restaurantId')
    }
    // Clear branchId when restaurant changes
    newParams.delete('branchId')
    setSearchParams(newParams, { replace: true })
    // Invalidate dashboard query to trigger refetch
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    // Invalidate user preferences to ensure fresh context-specific settings
    queryClient.invalidateQueries({ queryKey: ['user-preferences'] })
  }

  const handleBranchChange = (newBranchId: string | null) => {
    // Update store with new branch selection
    if (newBranchId) {
      setSelectedBranch({ id: newBranchId, name: '' })
    } else {
      setSelectedBranch(null)
    }
    // Update branchId in URL to reflect the new selection
    const newParams = new URLSearchParams(searchParams)
    if (newBranchId) {
      newParams.set('branchId', newBranchId)
    } else {
      newParams.delete('branchId')
    }
    setSearchParams(newParams, { replace: true })
    // Invalidate dashboard query to trigger refetch with new branchId
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    // Invalidate menu items query if user is on menu items page
    queryClient.invalidateQueries({ queryKey: ['menu-items'] })
    // Invalidate user preferences to ensure fresh context-specific settings
    queryClient.invalidateQueries({ queryKey: ['user-preferences'] })
  }

  // Validate URL params on mount and redirect if invalid
  useEffect(() => {
    // Branch manager: Remove any URL params (they shouldn't have context switching)
    if (role === 'branch_manager') {
      if (restaurantIdFromUrl) {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('restaurantId')
        setSearchParams(newParams, { replace: true })
      }
    }
    // Restaurant admin: Remove restaurantId param (they can't switch restaurants)
    else if (role === 'restaurant_admin') {
      if (restaurantIdFromUrl) {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('restaurantId')
        setSearchParams(newParams, { replace: true })
      }
    }
  }, [role, restaurantIdFromUrl, searchParams, setSearchParams])

  const handleLogout = async () => {
    if (!isLoaded || !signOut) {
      return
    }

    try {
      await signOut()
      clearAuth()
      navigate('/admin/login')
    } catch (error) {
      // Logout failed - still clear local auth and redirect to login
      // This ensures user can re-authenticate even if Clerk signOut fails
      clearAuth()
      navigate('/admin/login')
      // Error is handled gracefully - user will be redirected to login page
    }
  }

  return (
    <div 
      className="flex h-screen"
      style={{
        backgroundColor: 'var(--background)',
        color: 'var(--text-primary)',
      }}
    >
      {/* Sidebar */}
      <aside
        className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0"
        data-testid="admin-sidebar"
      >
        <div 
          className="flex flex-col flex-grow overflow-y-auto"
          style={{
            backgroundColor: 'var(--card-bg)',
            borderRight: '1px solid var(--border)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <div 
            className="flex items-center justify-between flex-shrink-0 px-6 py-4"
            style={{
              borderBottom: '1px solid var(--border)',
            }}
          >
            <h2 
              className="text-xl font-semibold"
              style={{
                fontFamily: 'var(--font-heading)',
                color: 'var(--text-primary)',
              }}
            >
              {t('dashboard.title')}
            </h2>
          </div>
          
          {/* User Profile - positioned at top of sidebar */}
          <UserProfile />
          
          {/* Context Selector in Sidebar */}
          <div 
            className="px-4 py-4"
            style={{
              borderBottom: '1px solid var(--border)',
            }}
          >
            <DashboardContext
              role={role}
              restaurantId={currentRestaurantId}
              restaurantName={restaurantName}
              branchId={selectedBranchIdFromStore || currentBranchId}
              branchName={branchName}
              onRestaurantChange={handleRestaurantChange}
              onBranchChange={handleBranchChange}
              isLoading={isLoadingDashboard}
              error={null}
              compact={true}
            />
          </div>

          <nav className="flex-1 px-4 py-4 space-y-2">
            {navigationItems
              .filter((item) => {
                // Filter navigation items based on user role
                // Branch Manager: Only Dashboard, Menu Items, Orders
                if (isBranchManager) {
                  return ['/admin/dashboard', '/admin/menu-items', '/admin/dishes', '/admin/orders'].includes(item.path)
                }
                // Restaurant Admin: All except Restaurants (super admin only)
                if (role === 'restaurant_admin') {
                  return item.path !== '/admin/restaurants'
                }
                // Super Admin: All items
                return true
              })
              .map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
            // Preserve only restaurantId in URL (not branchId)
            const restaurantParam = restaurantIdFromUrl ? `restaurantId=${restaurantIdFromUrl}` : ''
            const to = restaurantParam ? `${item.path}?${restaurantParam}` : item.path
            const label = t(item.labelKey)
              return (
                <Link
                  key={item.path}
                  to={to}
                  className={cn(
                    'flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors',
                    isActive
                      ? ''
                      : ''
                  )}
                  style={{
                    backgroundColor: isActive ? 'var(--primary)' : 'transparent',
                    color: isActive ? 'var(--cream-light)' : 'var(--text-secondary)',
                    borderRadius: 'var(--radius-md)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'var(--cream-medium)'
                      e.currentTarget.style.color = 'var(--text-primary)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent'
                      e.currentTarget.style.color = 'var(--text-secondary)'
                    }
                  }}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="mr-3 h-5 w-5 rtl:mr-0 rtl:ml-3" />
                  {label}
                </Link>
              )
            })}
          </nav>
          <div 
            className="flex-shrink-0 p-4 space-y-3"
            style={{
              borderTop: '1px solid var(--border)',
            }}
          >
            <LanguageSwitcher />
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full"
              data-testid="sidebar-logout-button"
            >
              <LogOut className="mr-2 h-4 w-4 rtl:mr-0 rtl:ml-2" />
              {t('common:buttons.logout', { defaultValue: 'Logout' })}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Navigation */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50"
        style={{
          backgroundColor: 'var(--card-bg)',
          borderTop: '1px solid var(--border)',
          boxShadow: 'var(--shadow-card)',
        }}
        data-testid="admin-mobile-nav"
      >
        <div className="flex justify-around items-center h-16">
          {navigationItems
            .filter((item) => {
              // Filter navigation items based on user role
              // Branch Manager: Only Dashboard, Menu Items, Orders
              if (isBranchManager) {
                return ['/admin/dashboard', '/admin/menu-items', '/admin/dishes', '/admin/orders'].includes(item.path)
              }
              // Restaurant Admin: All except Restaurants (super admin only)
              if (role === 'restaurant_admin') {
                return item.path !== '/admin/restaurants'
              }
              // Super Admin: All items
              return true
            })
            .map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path
            // Preserve only restaurantId in URL (not branchId)
            const restaurantParam = restaurantIdFromUrl ? `restaurantId=${restaurantIdFromUrl}` : ''
            const to = restaurantParam ? `${item.path}?${restaurantParam}` : item.path
            return (
              <Link
                key={item.path}
                to={to}
                className="flex flex-col items-center justify-center flex-1 h-full transition-colors"
                style={{
                  color: isActive ? 'var(--accent-gold)' : 'var(--text-secondary)',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'var(--text-primary)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'var(--text-secondary)'
                  }
                }}
                data-testid={`mobile-nav-${t(item.labelKey).toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="h-5 w-5 mb-1" />
                <span className="text-xs">{t(item.labelKey)}</span>
              </Link>
            )
          })}
        </div>
      </nav>

      {/* Main Content */}
      <div 
        className="flex-1 md:ml-64 pb-16 md:pb-0 rtl:md:ml-0 rtl:md:mr-64"
        style={{
          backgroundColor: 'var(--background)',
        }}
      >
        <main className="h-full overflow-y-auto">{children}</main>
      </div>
    </div>
  )
}

